import type { ConflictResolution } from "./dto.ts";

/** Marks a task created to resolve another task's merge-queue failure. */
export const MERGE_RESOLVE_LABEL = "merge-resolve";

/**
 * A task the merge queue will take once it is done and not already merging: it has a branch
 * (including a no-code task that still carries one) and it is not a resolve-merge chore.
 * The task page and `splitMergeQueue` both use this, so the headline and the queue cannot drift.
 */
export function mergeQueueHandles(t: { branch?: string | null; labels: readonly string[] }): boolean {
  return !!t.branch && !t.labels.includes(MERGE_RESOLVE_LABEL);
}

const CONFLICT_LINE = /^CONFLICT \([^)]*\):\s*(?:Merge conflict in\s+(.+)|.*?\s(\S+)\s(?:deleted|added) in)/;

/**
 * File paths named in a `git merge` conflict's output, de-duplicated and sorted. A failing
 * `checkCommand` (no actual merge conflict) names no files; callers fall back to other context.
 */
export function parseMergeConflicts(output: string | null): string[] {
  if (!output) return [];
  const files = new Set<string>();
  for (const line of output.split("\n")) {
    const m = CONFLICT_LINE.exec(line);
    const file = m?.[1] ?? m?.[2];
    if (file) files.add(file.trim());
  }
  return [...files].sort();
}

/** A human-readable summary of a merge-queue failure for a resolve task's description. */
export function describeMergeFailure(output: string | null, truncate = 6000): { files: string[]; summary: string; output: string } {
  const files = parseMergeConflicts(output);
  const clipped = (output ?? "").slice(0, truncate);
  // A real conflict (e.g. rename/rename) can fail CONFLICT_LINE's file extraction without failing
  // to indicate a conflict at all: check for that line independently so the summary never claims a
  // clean merge ("checkCommand failed") for what was actually an unparsed conflict.
  const hasConflict = output != null && /^CONFLICT \(/m.test(output);
  const summary = files.length
    ? `${files.length} conflicting file${files.length === 1 ? "" : "s"}: ${files.join(", ")}`
    : hasConflict
      ? "a merge conflict (see the output below for details)"
      : output
        ? "merged cleanly, but the project's checkCommand failed"
        : "merge failed with no output";
  return { files, summary, output: clipped };
}

/** Source files whose conflicts may be auto-resolved (#901); data, lock and config files are never touched. */
export const AUTO_RESOLVE_FILE = /\.(?:m|c)?tsx?$/;

/**
 * Deterministic resolution of a file's `diff3`-style conflicts (markers with a base section) when every
 * conflict is a pure append: both sides inserted lines at the same spot and the base there is empty (new
 * migration entries at the end of a list, new imports, exports or route registrations). The result keeps main's
 * lines (ours) first, then the branch's (theirs). Null, so the conflict goes to a resolve task, when any
 * hunk changes or deletes existing lines, when the two sides add a line in common (a likely duplicate), or
 * when the markers are not well formed.
 */
export function resolveAppendOnlyConflicts(text: string): string | null {
  const lines = text.split("\n");
  const out: string[] = [];
  let hunks = 0;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (!line.startsWith("<<<<<<< ")) {
      if (line === "=======" || line.startsWith(">>>>>>> ") || line.startsWith("||||||| ")) return null;
      out.push(line);
      continue;
    }
    const ours: string[] = [];
    const base: string[] = [];
    const theirs: string[] = [];
    let part: string[] = ours;
    let stage = 0;
    for (i++; i < lines.length; i++) {
      const l = lines[i]!;
      if (l.startsWith("<<<<<<< ")) return null;
      if (stage === 0 && l.startsWith("||||||| ")) {
        part = base;
        stage = 1;
      } else if (stage <= 1 && l === "=======") {
        if (stage === 0) return null; // no base section: not a diff3 conflict
        part = theirs;
        stage = 2;
      } else if (stage === 2 && l.startsWith(">>>>>>> ")) {
        stage = 3;
        break;
      } else part.push(l);
    }
    if (stage !== 3 || base.length > 0 || ours.length === 0 || theirs.length === 0) return null;
    const mine = new Set(ours.filter((l) => l.trim() !== ""));
    if (theirs.some((l) => l.trim() !== "" && mine.has(l))) return null;
    // Two entries with the same number (migration versions, ids) are a collision, not two appends.
    const numbers = (ls: string[]) => new Set(ls.flatMap((l) => l.match(/\d+/g) ?? []));
    const oursNumbers = numbers(ours);
    if ([...numbers(theirs)].some((n) => oursNumbers.has(n))) return null;
    out.push(...ours, ...theirs);
    hunks++;
  }
  return hunks > 0 ? out.join("\n") : null;
}

/** What a `merge_failed` report was: a git conflict, a failing checkCommand after a clean merge, or neither (no output). */
export type MergeFailureKind = "conflict" | "check" | "unknown";

export function classifyMergeFailure(output: string | null): MergeFailureKind {
  if (output == null || output.trim() === "") return "unknown";
  if (/^CONFLICT \(/m.test(output) || /Automatic merge failed/i.test(output)) return "conflict";
  return "check";
}

/** Bounces back to the builder before the hub gives up and files a resolve task (landing A1). */
export const MERGE_REWORK_MAX = 2;

export type MergeReworkRoute =
  /** The task goes back to its builder, who merges main into the branch in the same worktree. */
  | "builder"
  /** A resolve-merge task for the office's merge agent (today's path). */
  | "office"
  /** A resolve-merge task that nobody is assigned to (`conflictResolution: off`). */
  | "unassigned";

/**
 * Where a merge failure goes. A conflict or a failing check (the batch check, after a lone retry) can go back
 * to the builder, who fixes it in their own session; only when the project says so, the builder is an active
 * agent, and the task has not bounced MERGE_REWORK_MAX times already. A resolve task itself
 * never routes anywhere (its own failure is handled by the stuck-merge escalation).
 */
export function mergeReworkRoute(input: {
  kind: MergeFailureKind;
  conflictResolution: ConflictResolution;
  builder: { kind: string; active: boolean } | null;
  reworkCount: number;
}): MergeReworkRoute {
  if (input.conflictResolution === "off") return "unassigned";
  if (input.conflictResolution === "builder" && (input.kind === "conflict" || input.kind === "check") && input.builder?.kind === "agent" && input.builder.active && input.reworkCount < MERGE_REWORK_MAX) return "builder";
  return "office";
}

const FAILING_LINE = /^\s*(?:FAIL\b|[×✗✘✖]|AssertionError|(?:Type|Reference|Syntax)?Error:|error TS\d+|not ok\b)/;

/** The lines of a failing check's output that name what failed (vitest FAIL lines, assertion and type errors), de-duplicated and capped, for a rework note. */
export function failingCheckLines(output: string | null, max = 8): string[] {
  if (!output) return [];
  const seen = new Set<string>();
  for (const raw of output.split("\n")) {
    const line = raw.trim().slice(0, 240);
    if (line && FAILING_LINE.test(line)) seen.add(line);
    if (seen.size >= max) break;
  }
  return [...seen];
}

/** The merge-queue WIP cap (landing A1 D5): agents stop claiming while `queued` done tasks wait to land. */
export function mergeBacklogFull(backlog: { queued: number; cap: number | null }): boolean {
  return backlog.cap !== null && backlog.cap >= 0 && backlog.queued >= backlog.cap;
}
